import { jsx as _jsx, Fragment as _Fragment } from "react/jsx-runtime";
import { _className, ComponentSync } from '@nu-art/thunderstorm-frontend';
import { parseExpression_Normalized } from '@app/hcs-shared/_entity/expression/antlr/parser';
import { BadImplementationException, cloneObj, compare, exists, voidFunction } from '@nu-art/ts-common';
import './Component_PrettyPrint_Expression.scss';
import { FadeScrollWrapper } from '@app/styles';
import { Component_PrettyPrint_Term } from '../Component_PrettyPrint_Term/Component_PrettyPrint_Term.js';
import { ExpressionType_ResolutionFunction, LogicType_StructuredFreeText } from '@app/hcs-shared';
import { Component_PrettyPrint_FreeTextTerm } from '../Component_PrettyPrint_FreeTextTerm/Component_PrettyPrint_FreeTextTerm.js';
import { Component_PrettyPrint_Number } from '../generic/Component_PrettyPrint_Number/Component_PrettyPrint_Number.js';
import { Component_PrettyPrint_Unary } from '../generic/Component_PrettyPrint_Unary/Component_PrettyPrint_Unary.js';
import { Component_PrettyPrint_Binary } from '../generic/Component_PrettyPrint_Binary/Component_PrettyPrint_Binary.js';
import { Component_PrettyPrint_Variadic } from '../generic/Component_PrettyPrint_Variadic/Component_PrettyPrint_Variadic.js';
import { Component_PrettyPrint_Comparison } from '../generic/Component_PrettyPrint_Comparison/Component_PrettyPrint_Comparison.js';
import { Component_PrettyPrint_Range } from '../generic/Component_PrettyPrint_Range/Component_PrettyPrint_Range.js';
export class Component_PrettyPrint_Expression extends ComponentSync {
    //######################### Life Cycle #########################
    deriveStateFromProps(props, state) {
        state.expression = cloneObj(props.expression);
        state.interactive = props.interactive;
        state.guidelines = !!props.guidelines;
        if ('interactiveProperties' in props) {
            state.suggestedValueMap = props.interactiveProperties?.suggestedValueMap;
            state.calculationResult = props.interactiveProperties?.calculationResult;
            state.actualValueMap = props.interactiveProperties?.actualValueMap;
            state.selectedVariableId = props.interactiveProperties?.selectedVariableId;
            state.partialValueMap = props.interactiveProperties?.partialValueMap;
            state.valueTree = props.interactiveProperties?.valueTree;
        }
        else {
            delete state.suggestedValueMap;
            delete state.calculationResult;
            delete state.actualValueMap;
            delete state.selectedVariableId;
            delete state.partialValueMap;
            delete state.valueTree;
        }
        if (!compare(this.state?.expression, state.expression))
            try {
                state.description = parseExpression_Normalized(state.expression.expression);
                state.parseError = undefined;
            }
            catch (err) {
                state.parseError = err;
                state.description = undefined;
            }
        return state;
    }
    shouldReDeriveState(nextProps) {
        return super.shouldReDeriveState(nextProps)
            || !compare(nextProps.expression, this.state?.expression);
    }
    //######################### Logic #########################
    //######################### Render #########################
    render() {
        if (this.state.description) {
            const className = _className('c__expression-pretty-print', !this.state.guidelines && 'no-guide-lines');
            return _jsx(FadeScrollWrapper, { className: className, children: this.render_Expression(this.state.description, this.state.valueTree) });
        }
        //Special exception when parsing fails:
        //"resolution-function" type expressions should show a custom message when all they output is "true"
        if (this.state.expression.expressionType === ExpressionType_ResolutionFunction.dbKey && this.state.expression.expression === 'true')
            return _jsx("div", { className: 'c__expression-pretty-print__message', children: "As all the other conditions were not met, this is the recommended next step" });
        return _jsx("div", { className: 'c__expression-pretty-print-error', children: this.state.parseError?.message ?? 'No description or error to present' });
    }
    render_Expression = (exp, valueNode) => {
        switch (exp.type) {
            case 'term':
                return this.render_TermExpression(exp);
            case 'number':
                return _jsx(Component_PrettyPrint_Number, { expression: exp, valueNode: valueNode, descriptorRenderer: this.render_Expression, interactive: this.state.interactive });
            case 'unary':
                return _jsx(Component_PrettyPrint_Unary, { expression: exp, valueNode: valueNode, descriptorRenderer: this.render_Expression, interactive: this.state.interactive });
            case 'binary':
                return _jsx(Component_PrettyPrint_Binary, { expression: exp, valueNode: valueNode, descriptorRenderer: this.render_Expression, interactive: this.state.interactive });
            case 'variadic':
                return _jsx(Component_PrettyPrint_Variadic, { expression: exp, valueNode: valueNode, descriptorRenderer: this.render_Expression, interactive: this.state.interactive });
            case 'comparison':
                return _jsx(Component_PrettyPrint_Comparison, { expression: exp, valueNode: valueNode, descriptorRenderer: this.render_Expression, interactive: this.state.interactive });
            case 'range':
                return _jsx(Component_PrettyPrint_Range, { expression: exp, valueNode: valueNode, descriptorRenderer: this.render_Expression, interactive: this.state.interactive });
            case 'group':
                if (exists(valueNode) && valueNode?.type !== 'group')
                    throw new BadImplementationException(`ValueTree_Node type mismatch: expected "group", got "${valueNode.type}"`);
                return exists(exp.expression) ? this.render_Expression(exp.expression, valueNode?.inner) : _jsx(_Fragment, {});
        }
    };
    //######################### Render - Expression types #########################
    render_TermExpression = (exp) => {
        const term = this.state.expression.terms[exp.termIndex - 1];
        const termResult = this.state.calculationResult?.[`T${exp.termIndex}`];
        const onVariableSelected = 'interactiveProperties' in this.props ? this.props.interactiveProperties.onVariableSelected : voidFunction;
        if (term?.atomsLogic === LogicType_StructuredFreeText)
            return _jsx(Component_PrettyPrint_FreeTextTerm, { modules: this.props.modules, interactive: this.state.interactive, term: term, termIndex: exp.termIndex, interactiveProperties: {
                    actualValueMap: this.state.actualValueMap,
                    suggestedValueMap: this.state.suggestedValueMap,
                    onVariableSelected: onVariableSelected,
                    calculationResult: termResult,
                    selectedVariableId: this.state.selectedVariableId,
                } });
        return _jsx(Component_PrettyPrint_Term, { term: term, modules: this.props.modules, termIndex: exp.termIndex, interactive: this.state.interactive, interactiveProperties: {
                actualValueMap: this.state.actualValueMap,
                suggestedValueMap: this.state.suggestedValueMap,
                onVariableSelected: onVariableSelected,
                calculationResult: termResult,
                selectedVariableId: this.state.selectedVariableId,
                partialValueMap: this.state.partialValueMap,
            } });
    };
}
//# sourceMappingURL=Component_PrettyPrint_Expression.js.map